import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ImageComponent } from '../../atoms/image/image.component';
import { LinkComponent } from '../../atoms/link/link.component';
import { ButtonComponent } from '../../molecules/button/button.component';
import { IconComponent } from '../../atoms/icon/icon.component';
import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { HrefType } from '../../../shared/enums/href-type.enums';
import {
  CaasInternalLink,
  CaasLink,
  CaasStage,
  CaasTemplate,
} from '../../../shared/interfaces/caas/caas.interface';
import { Router } from '@angular/router';
import { ImageMetaData } from '../../../shared/interfaces/meta-data.interface';
import { ImagesService } from '../../../services/images.service';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';

@Component({
  selector: 'hdi-home-stage',
  standalone: true,
  templateUrl: './home-stage.component.html',
  styleUrl: './home-stage.component.scss',
  imports: [
    CommonModule,
    ImageComponent,
    LinkComponent,
    ButtonComponent,
    IconComponent,
    CapitalizePipe,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class HomeStageComponent implements OnChanges {
  Sizes = Sizes;
  BgColors = Colors;
  ButtonType = ButtonType;

  @Input() buttonType: ButtonType = ButtonType.SECONDARY;
  @Input() iconButton = 'arrow-right';
  @Input() teaser1_header = '';
  @Input() teaser1_image = '';
  @Input() teaser1_hrefType: HrefType = HrefType.ANGULAR_ROUTE;
  @Input() teaser1_href = '';
  @Input() teaser1_fragment = '';
  @Input() teaser1_title = '';
  @Input() teaser1_label = '';
  @Input() teaser1_imageTitle = '';
  @Input() teaser1_imageAlt = '';
  @Input() teaser2_header = '';
  @Input() teaser2_icon = 'user-lock';
  @Input() teaser2_hrefType: HrefType = HrefType.ANGULAR_ROUTE;
  @Input() teaser2_href = '';
  @Input() teaser2_fragment = '';
  @Input() teaser2_label = '';
  @Input() teaser2_title = '';
  @Input() teaser3_header = '';
  @Input() teaser3_image = '';
  @Input() teaser3_hrefType: HrefType = HrefType.ANGULAR_ROUTE;
  @Input() teaser3_href = '';
  @Input() teaser3_fragment = '';
  @Input() teaser3_label = '';
  @Input() teaser3_title = '';
  @Input() teaser3_imageTitle = '';
  @Input() teaser3_imageAlt = '';
  @Input() caasData?: CaasStage;
  @Input() imagesMetaData?: ImageMetaData[];

  constructor(
    private cdr: ChangeDetectorRef,
    private caasBodytextService: CaasBodytextService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private imagesService: ImagesService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imagesMetaData']?.currentValue) {
      this.imagesService.setImagesMetaData(
        changes['imagesMetaData'].currentValue
      );
    }
    if (
      changes['caasData']?.currentValue &&
      this.caasData &&
      Object.keys(this.caasData || {}).length
    ) {
      this.setCaasContent();
      this.cdr.detectChanges();
    }
  }

  setCaasContent(): void {
    this.teaser1_header =
      this.caasData?.formData?.st_teaser1_header?.value || this.teaser1_header;
    this.teaser1_image =
      this.caasData?.formData?.st_teaser1_image?.value?.url ||
      this.teaser1_image;
    this.teaser1_hrefType = this.determineHrefType(
      this.caasData?.formData?.st_teaser1_link
    );
    this.teaser1_href =
      this.determineHref(this.caasData?.formData?.st_teaser1_link) || '/404';
    this.teaser1_fragment =
      this.caasData?.formData?.st_teaser1_link?.value?.formData?.st_anchor_name
        ?.value || '';
    this.teaser1_label =
      this.caasData?.formData?.st_teaser1_link?.value?.formData?.lt_link_label
        ?.value || '';
    this.teaser1_title =
      this.caasData?.formData?.st_teaser1_link?.value?.formData?.lt_link_title
        ?.value || '';
    this.teaser1_imageAlt =
      this.imagesService.getAltTextById(
        this.caasData?.formData?.st_teaser1_image?.value?.identifier ?? ''
      ) ?? '';
    this.teaser1_imageTitle =
      this.caasData?.formData?.st_teaser1_image_title?.value ?? '';
    this.teaser2_header =
      this.caasData?.formData?.st_teaser2_header?.value || this.teaser2_header;
    this.teaser2_icon =
      this.caasData?.formData?.st_teaser2_icon?.value || 'user-lock';
    this.teaser2_hrefType = this.determineHrefType(
      this.caasData?.formData?.st_teaser2_link
    );
    this.teaser2_href =
      this.determineHref(this.caasData?.formData?.st_teaser2_link) ||
      '/services';
    this.teaser2_label =
      this.caasData?.formData?.st_teaser2_link?.value?.formData?.lt_link_label
        ?.value || this.teaser2_label;
    this.teaser2_title =
      this.caasData?.formData?.st_teaser2_link?.value?.formData?.lt_link_title
        ?.value || '';
    this.teaser2_fragment =
      this.caasData?.formData?.st_teaser2_link?.value?.formData?.st_anchor_name
        ?.value || '';
    this.teaser3_header =
      this.caasData?.formData?.st_teaser3_header?.value || this.teaser3_header;
    this.teaser3_image =
      this.caasData?.formData?.st_teaser3_image?.value?.url ||
      this.teaser3_image;
    this.teaser3_hrefType = this.determineHrefType(
      this.caasData?.formData?.st_teaser3_link
    );
    this.teaser3_href =
      this.determineHref(this.caasData?.formData?.st_teaser3_link) || '';
    this.teaser3_label =
      this.caasData?.formData?.st_teaser3_link?.value?.formData?.lt_link_label
        ?.value || this.teaser3_label;
    this.teaser3_title =
      this.caasData?.formData?.st_teaser3_link?.value?.formData?.lt_link_title
        ?.value || '';
    this.teaser3_fragment =
      this.caasData?.formData?.st_teaser3_link?.value?.formData?.st_anchor_name
        ?.value || '';
  }

  /**
   * Determines the href type based on the provided form data.
   * @param link - The link form data.
   * @returns The HrefType.
   */
  private determineHrefType(
    link:
      | {
          fsType: string;
          name: string;
          value: {
            formData: {
              lt_link_label: { fsType: string; name: string; value: string };
              lt_link_external: {
                fsType: string;
                name: string;
                value: string | null;
              };
              lt_link_internal: CaasInternalLink;
              lt_link_title: { fsType: string; name: string; value: string };
              lt_link_type: {
                fsType: string;
                name: string;
                value: { fsType: string; identifier: string; label: string };
              };
              lt_link_video_type: {
                fsType: string;
                name: string;
                value: { fsType: string; identifier: string; label: string };
              };
              lt_link_vimeo_id: {
                fsType: string;
                name: string;
                value: string | null;
              };
              lt_link_youtube_id: {
                fsType: string;
                name: string;
                value: string | null;
              };
            };
            template: CaasTemplate;
          };
        }
      | {
          fsType: string;
          name: string;
          value: {
            formData: {
              lt_button_label: { fsType: string; name: string; value: string };
              lt_link_external: {
                fsType: string;
                name: string;
                value: string | null;
              };
              lt_link_internal: CaasInternalLink;
              lt_link_label: { fsType: string; name: string; value: string };
              lt_link_type: {
                fsType: string;
                name: string;
                value: { fsType: string; identifier: string; label: string };
              };
              lt_link_video_type: {
                fsType: string;
                name: string;
                value: { fsType: string; identifier: string; label: string };
              };
              lt_link_vimeo_id: {
                fsType: string;
                name: string;
                value: string | null;
              };
              lt_link_youtube_id: {
                fsType: string;
                name: string;
                value: string | null;
              };
            };
            template: CaasTemplate;
          };
        }
      | CaasLink
      | undefined
  ): HrefType {
    if (!link || !link.value?.formData) {
      console.warn('Invalid link structure:', link);
      return HrefType.ANGULAR_ROUTE;
    }

    const formData = link.value.formData;

    if (formData.lt_link_external?.value) {
      return HrefType.EXTERNAL;
    }

    // Otherwise, fallback to the link type identifier
    const linkType = formData.lt_link_type?.value?.identifier;

    return linkType === 'external' ? HrefType.EXTERNAL : HrefType.ANGULAR_ROUTE;
  }
  /**
   * Determines the href value based on internal and external link fields.
   * @param link - The link form data.
   * @returns The href value.
   */
  private determineHref(
    link:
      | {
          fsType: string;
          name: string;
          value: {
            formData: {
              lt_button_label: { fsType: string; name: string; value: string };
              lt_link_external: {
                fsType: string;
                name: string;
                value: string | null;
              };
              lt_link_internal: CaasInternalLink;
              lt_link_label: { fsType: string; name: string; value: string };
              lt_link_type: {
                fsType: string;
                name: string;
                value: { fsType: string; identifier: string; label: string };
              };
              lt_link_video_type: {
                fsType: string;
                name: string;
                value: { fsType: string; identifier: string; label: string };
              };
              lt_link_vimeo_id: {
                fsType: string;
                name: string;
                value: string | null;
              };
              lt_link_youtube_id: {
                fsType: string;
                name: string;
                value: string | null;
              };
            };
            template: CaasTemplate;
          };
        }
      | CaasLink
      | {
          fsType: string;
          name: string;
          value: {
            formData: {
              lt_link_label: { fsType: string; name: string; value: string };
              lt_link_external: {
                fsType: string;
                name: string;
                value: string | null;
              };
              lt_link_internal: CaasInternalLink;
              lt_link_title: { fsType: string; name: string; value: string };
              lt_link_type: {
                fsType: string;
                name: string;
                value: {
                  fsType: string;
                  identifier: string;
                  label: string;
                };
              };
              lt_link_video_type: {
                fsType: string;
                name: string;
                value: {
                  fsType: string;
                  // Inputs for dynamic data
                  identifier: string;
                  label: string;
                };
              };
              lt_link_vimeo_id: {
                fsType: string;
                name: string;
                value: string | null;
              };
              lt_link_youtube_id: {
                fsType: string;
                name: string;
                value: string | null;
              };
            };
            template: CaasTemplate;
          };
        }
      | undefined
  ): string | null {
    if (!link) return null;
    const externalLink = link.value?.formData?.lt_link_external?.value;
    if (externalLink) return externalLink;

    const internalLink = link.value?.formData?.lt_link_internal?.value;
    return internalLink ? this.getInternalUrl(link as CaasLink) : null;
  }

  private getInternalUrl(internalLink: CaasLink): string {
    return this.caasBodytextService.foundHref(internalLink);
  }
}
